import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LangContext";
import { useCurrency } from "../contexts/CurrencyContext";
import { TIERS } from "../lib/i18n";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Wallet as WalletIcon, Coins, Trophy, ShieldAlert, Plus, Eye, Share2, Copy, Zap, Award } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const { fmt } = useCurrency();
  const [pools, setPools] = useState([]);
  const [walletData, setWalletData] = useState({ balance: 0, coins: 0, transactions: [] });

  useEffect(() => {
    (async () => {
      const [p, w] = await Promise.all([api.get("/pools"), api.get("/wallet")]);
      setPools(p.data); setWalletData(w.data);
    })();
  }, []);

  const myPools = pools.filter((p) => p.members.some((m) => m.user_id === user?.user_id));
  const totalWinnings = walletData.transactions.filter((tx) => tx.type === "pool_winnings").reduce((s, tx) => s + tx.amount, 0);
  const tier = user?.tier || "bronze";
  const tierInfo = TIERS[tier];
  const xp = user?.xp || 0;
  const nextThreshold = user?.next_tier_xp || xp;
  const prevThreshold = tier === "gold" ? 25000 : tier === "silver" ? 5000 : 0;
  const progress = user?.next_tier ? Math.min(100, ((xp - prevThreshold) / (nextThreshold - prevThreshold)) * 100) : 100;

  const copyRef = () => {
    navigator.clipboard.writeText(user?.referral_code || "");
    toast.success(t("copied"));
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex flex-wrap items-baseline justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-cyan-gradient">{t("dashboard")}</h1>
          <p className="text-sm text-[#8FA3BD] mt-1">{user?.name}</p>
        </div>
        {user?.role === "admin" && (
          <Link to="/pools/new"><Button className="btn-cyan" data-testid="create-pool-btn"><Plus className="w-4 h-4 me-1.5" /> {t("createPool")}</Button></Link>
        )}
      </div>

      {user?.kyc_status !== "approved" && (
        <Link to="/kyc" className="block mb-6">
          <div className="p-4 rounded-xl border border-[#D4AF37]/40 bg-[#D4AF37]/5 flex items-center gap-3 hover:bg-[#D4AF37]/10" data-testid="kyc-warning-banner">
            <ShieldAlert className="w-5 h-5 text-[#D4AF37]" />
            <div>
              <div className="text-sm font-semibold text-[#D4AF37]">{t("requiredKyc")}</div>
              <div className="text-xs text-[#8FA3BD]">{t("pendingKyc")}</div>
            </div>
          </div>
        </Link>
      )}

      {/* Balance + Tier Card (hero of dashboard) */}
      <div className="grid lg:grid-cols-3 gap-5 mb-6">
        {/* Balance */}
        <div className="lg:col-span-2 bento-card p-7 relative overflow-hidden" data-testid="balance-hero-card">
          <div className="absolute -top-20 -end-20 w-64 h-64 rounded-full bg-[#00E5FF]/10 blur-3xl pointer-events-none" />
          <div className="relative">
            <div className="flex items-center gap-2 text-xs text-[#8FA3BD] mb-2">
              <WalletIcon className="w-4 h-4 text-[#00E5FF]" /> {t("yourBalance")}
            </div>
            <div className="text-5xl sm:text-6xl font-bold text-cyan-gradient" data-testid="dashboard-balance">{fmt(walletData.balance)}</div>
            <div className="flex gap-4 mt-5">
              <Link to="/wallet"><Button size="sm" className="btn-cyan" data-testid="dash-deposit-btn">{t("deposit")}</Button></Link>
              <Link to="/wallet"><Button size="sm" variant="outline" className="border-[#1A2D4D] text-white">{t("withdraw")}</Button></Link>
            </div>
          </div>
        </div>

        {/* Tier + XP */}
        <div className="bento-card p-6 relative" data-testid="tier-card">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs text-[#8FA3BD]">{t("yourTier")}</div>
            <span className={`text-xs font-bold px-3 py-1 rounded-full ${tierInfo.cls}`}>
              <Award className="w-3 h-3 inline me-1" /> {lang === "ar" ? tierInfo.label_ar : tierInfo.label_en}
            </span>
          </div>
          <div className="flex items-baseline gap-2">
            <Zap className="w-5 h-5 text-[#FF9500]" />
            <span className="text-3xl font-bold text-white" data-testid="dashboard-xp">{xp.toLocaleString()}</span>
            <span className="text-xs text-[#8FA3BD]">XP</span>
          </div>
          {user?.next_tier ? (
            <>
              <div className="xp-bar mt-4"><div className="xp-bar-fill" style={{ width: `${progress}%` }} /></div>
              <div className="text-xs text-[#8FA3BD] mt-2">
                {nextThreshold - xp} XP → <span className="text-[#00E5FF]">{lang === "ar" ? TIERS[user.next_tier].label_ar : TIERS[user.next_tier].label_en}</span>
              </div>
            </>
          ) : (
            <div className="text-xs text-[#D4AF37] mt-3">🏆 Max tier reached</div>
          )}
          <div className="text-[10px] text-[#8FA3BD] mt-2">{t("maxPool")}: {tierInfo.max_pool === 999999 ? "∞" : `$${tierInfo.max_pool}`}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Coins} label={t("coins")} value={walletData.coins} testid="stat-coins" />
        <StatCard icon={Trophy} label={t("winnings")} value={fmt(totalWinnings)} testid="stat-winnings" />
        <StatCard icon={Plus} label={t("activePool")} value={myPools.length} testid="stat-active-pools" />
        <StatCard icon={Zap} label={t("xp")} value={xp.toLocaleString()} testid="stat-xp" />
      </div>

      {/* Referral card */}
      <div className="bento-card p-5 mb-8 flex flex-wrap items-center gap-4" data-testid="referral-card">
        <Share2 className="w-6 h-6 text-[#00E5FF]" />
        <div className="flex-1">
          <div className="text-xs text-[#8FA3BD]">{t("referralCode")}</div>
          <div className="text-xl font-bold text-cyan-gradient tracking-widest" data-testid="referral-code-text">{user?.referral_code}</div>
        </div>
        <Button onClick={copyRef} variant="outline" size="sm" className="border-[#1A2D4D] text-white" data-testid="copy-ref-btn">
          <Copy className="w-3.5 h-3.5 me-1.5" /> {t("copyRef")}
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <h2 className="text-lg font-semibold mb-4">{t("pools")}</h2>
          <div className="space-y-3">
            {pools.slice(0, 5).map((p) => (
              <Link key={p.pool_id} to={`/pools/${p.pool_id}`} className="block">
                <div className="bento-card p-5 flex items-center gap-4" data-testid={`pool-item-${p.pool_id}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{p.name}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${TIERS[p.tier || "bronze"].cls}`}>
                        {lang === "ar" ? TIERS[p.tier || "bronze"].label_ar : TIERS[p.tier || "bronze"].label_en}
                      </span>
                    </div>
                    <div className="text-xs text-[#8FA3BD] mt-1">
                      {fmt(p.amount_per_round)}/{t(p.frequency || "daily")} × {p.member_count} • <span className={p.status === "active" ? "text-[#00E676]" : p.status === "completed" ? "text-[#8FA3BD]" : "text-[#00E5FF]"}>{t(p.status)}</span>
                    </div>
                  </div>
                  <div className="text-end">
                    <div className="text-[#00E5FF] font-bold">{fmt(p.pot_per_round)}</div>
                    <div className="text-xs text-[#8FA3BD]">{p.members.length}/{p.member_count}</div>
                  </div>
                  <Eye className="w-4 h-4 text-[#4A5970]" />
                </div>
              </Link>
            ))}
            {pools.length === 0 && <div className="text-sm text-[#8FA3BD] p-6 text-center bento-card">{t("noPools")}</div>}
          </div>
        </div>

        <div>
          <h2 className="text-lg font-semibold mb-4">{t("recentActivity")}</h2>
          <div className="bento-card p-4 space-y-3">
            {walletData.transactions.slice(0, 6).map((tx) => (
              <div key={tx.tx_id} className="flex items-center justify-between text-sm">
                <div>
                  <div className="font-medium">{tx.type.replace("_", " ")}</div>
                  <div className="text-xs text-[#4A5970]">{tx.method}</div>
                </div>
                <div className={tx.amount > 0 ? "text-[#00E676]" : "text-[#8FA3BD]"}>
                  {tx.amount > 0 ? "+" : ""}{tx.amount}
                </div>
              </div>
            ))}
            {walletData.transactions.length === 0 && <div className="text-xs text-[#4A5970] text-center py-6">—</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="bento-card p-5" data-testid={testid}>
      <Icon className="w-5 h-5 text-[#00E5FF] mb-3" />
      <div className="text-2xl font-bold text-white">{value}</div>
      <div className="text-xs text-[#8FA3BD] mt-1">{label}</div>
    </div>
  );
}
